import React, { useEffect, useMemo, useState } from "react";
import { X, Image as ImageIcon } from "lucide-react";
import SakuraDivider from "@/components/SakuraDivider";
import { api } from "@/lib/api";

const CATEGORIES = ["All", "Cake Pops", "Party Sets", "Seasonal Treats", "Custom Themes"];

// Placeholder images — shown only if Kaitlyn hasn't uploaded her own photos yet.
const PLACEHOLDERS = [
  { src: "https://images.unsplash.com/photo-1555526148-5cd740b44241?auto=format&fit=crop&w=900&q=80", category: "Cake Pops", title: "Pastel pink cake pops" },
  { src: "https://images.pexels.com/photos/10171574/pexels-photo-10171574.jpeg?auto=compress&cs=tinysrgb&w=900", category: "Party Sets", title: "Party treat box" },
  { src: "https://images.unsplash.com/photo-1676734629121-2e824e3a1090?auto=format&fit=crop&w=900&q=80", category: "Seasonal Treats", title: "Cherry blossom pops" },
  { src: "https://images.pexels.com/photos/18060205/pexels-photo-18060205.jpeg?auto=compress&cs=tinysrgb&w=900", category: "Custom Themes", title: "Themed dessert box" },
  { src: "https://images.unsplash.com/photo-1587668178277-295251f900ce?auto=format&fit=crop&w=900&q=80", category: "Cake Pops", title: "Heart cake pops" },
  { src: "https://images.unsplash.com/photo-1486427944299-d1955d23e34d?auto=format&fit=crop&w=900&q=80", category: "Party Sets", title: "Dessert table" },
  { src: "https://images.unsplash.com/photo-1464347744102-11db6282f854?auto=format&fit=crop&w=900&q=80", category: "Custom Themes", title: "Floral bouquet pops" },
  { src: "https://images.unsplash.com/photo-1519869325930-281384150729?auto=format&fit=crop&w=900&q=80", category: "Seasonal Treats", title: "Holiday treats" },
];

export default function Gallery() {
  const [cat, setCat] = useState("All");
  const [open, setOpen] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/gallery").then(({ data }) => {
      if (!alive) return;
      setItems((data || []).map((d) => ({ src: d.data_url, category: d.category, title: d.title })));
    }).catch(() => {}).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const source = items.length > 0 ? items : PLACEHOLDERS;
  const filtered = useMemo(
    () => (cat === "All" ? source : source.filter((i) => i.category === cat)),
    [cat, source]
  );

  return (
    <div data-testid="page-gallery">
      <section className="section">
        <div className="mx-auto max-w-5xl text-center">
          <span className="chip">Gallery</span>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl text-sakura-ink">A little peek inside the sweet shop</h1>
          <p className="mt-4 text-sakura-soft max-w-2xl mx-auto">
            {items.length > 0
              ? "Filter by category below to browse past creations."
              : "Sample inspiration while Kaitlyn uploads her own photos — filter by category below."}
          </p>
          <SakuraDivider />
        </div>
      </section>

      <section className="section pt-0">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-wrap justify-center gap-2 mb-10" data-testid="gallery-tabs">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                data-testid={`gallery-tab-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition ${cat === c ? "bg-sakura-400 text-white shadow-sakura" : "bg-white border border-sakura-200 text-sakura-ink hover:bg-sakura-100"}`}
              >
                {c}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-16 text-sakura-soft">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-sakura-soft flex flex-col items-center gap-3">
              <ImageIcon className="h-8 w-8 text-sakura-300" />
              <span>No photos in this category yet.</span>
            </div>
          ) : (
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 md:gap-5" data-testid="gallery-grid">
              {filtered.map((img, i) => (
                <button
                  key={img.src + i}
                  onClick={() => setOpen(img)}
                  className="mb-4 md:mb-5 block w-full overflow-hidden rounded-3xl border border-sakura-200 shadow-soft group relative break-inside-avoid"
                  data-testid={`gallery-item-${i}`}
                >
                  <img src={img.src} alt={img.title || img.category} className="w-full h-auto object-cover transition duration-500 group-hover:scale-[1.03]" loading="lazy" />
                  <span className="absolute bottom-3 left-3 chip bg-white/90">{img.category}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {open && (
        <div
          role="dialog" aria-modal="true"
          className="fixed inset-0 z-[60] bg-sakura-ink/70 backdrop-blur grid place-items-center p-4"
          onClick={() => setOpen(null)}
          data-testid="gallery-lightbox"
        >
          <div className="relative max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              className="absolute -top-4 -right-4 h-10 w-10 grid place-items-center rounded-full bg-white text-sakura-ink shadow-sakura"
              onClick={() => setOpen(null)}
              aria-label="Close"
              data-testid="gallery-lightbox-close"
            >
              <X className="h-4 w-4" />
            </button>
            <img src={open.src} alt={open.title || open.category} className="w-full h-auto rounded-3xl shadow-sakuraStrong" />
            <div className="mt-3 text-center text-white/90 text-sm">{open.title ? `${open.title} · ` : ""}{open.category}</div>
          </div>
        </div>
      )}
    </div>
  );
}
